/**
 * Content Type Detection Utility
 * Detecteert het type content op basis van post data
 */

export type ContentType = 'video' | 'audio' | 'article' | 'live' | 'text' | 'mixed';

export interface ContentTypeResult {
  primaryType: ContentType;
  secondaryTypes: ContentType[];
  confidence: number; // 0-1
}

/**
 * Detecteert het primaire content type van een post
 */
export function detectContentType(post: any): ContentTypeResult {
  const content = String(post?.content || '').toLowerCase();
  const attachments = Array.isArray(post?.attachments) ? post.attachments : [];
  const types: Set<ContentType> = new Set();
  
  // Check voor video content
  const hasYouTube = /(youtube\.com|youtu\.be)/.test(content);
  const hasVimeo = /vimeo\.com/.test(content);
  const hasVideoEmbed = /<iframe[^>]*src="[^"]*video[^"]*"/.test(content);
  const hasVideoAttachment = attachments.some((a: any) => a?.type === 'video' || /\.(mp4|mov|avi|webm)$/i.test(a?.name || ''));
  const hasVideoKeyword = /\b(video|vlog|tutorial|webinar|recording)\b/i.test(content);
  
  if (hasYouTube || hasVimeo || hasVideoEmbed || hasVideoAttachment) {
    types.add('video');
  }
  
  // Check voor audio/podcast content
  const hasSpotify = /spotify\.com/.test(content);
  const hasApplePodcasts = /podcasts\.apple\.com/.test(content);
  const hasSoundCloud = /soundcloud\.com/.test(content);
  const hasAudioAttachment = attachments.some((a: any) => a?.type === 'audio' || /\.(mp3|wav|m4a|ogg)$/i.test(a?.name || ''));
  const hasPodcastKeyword = /\b(podcast|episode|luister|audio|interview)\b/i.test(content);
  
  if (hasSpotify || hasApplePodcasts || hasSoundCloud || hasAudioAttachment || hasPodcastKeyword) {
    types.add('audio');
  }
  
  // Check voor article/blog content
  const hasMedium = /medium\.com/.test(content);
  const hasLinkedInArticle = /linkedin\.com\/pulse/.test(content);
  const hasBlogKeyword = /\b(artikel|article|blog|lees meer|read more)\b/i.test(content);
  const hasLongText = content.length > 500;
  
  if (hasMedium || hasLinkedInArticle || hasBlogKeyword || hasLongText) {
    types.add('article');
  }
  
  // Check voor live sessies/events
  const hasZoom = /zoom\.us/.test(content);
  const hasTeams = /teams\.microsoft\.com/.test(content);
  const hasMeet = /meet\.google\.com/.test(content);
  const hasLiveKeyword = /\b(live|livestream|webinar|sessie|event|bijeenkomst)\b/i.test(content);
  const hasEventDate = /\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/.test(content);
  
  if (hasZoom || hasTeams || hasMeet || (hasLiveKeyword && hasEventDate)) {
    types.add('live');
  }
  
  // Determine primary and secondary types
  const typesArray = Array.from(types);
  
  if (typesArray.length === 0) {
    // No specific type detected, mark as text
    return {
      primaryType: 'text',
      secondaryTypes: [],
      confidence: 0.9
    };
  }
  
  if (typesArray.length === 1) {
    return {
      primaryType: typesArray[0],
      secondaryTypes: [],
      confidence: 0.85
    };
  }
  
  // Multiple types - mark as mixed, but return first detected as primary
  return {
    primaryType: typesArray[0],
    secondaryTypes: typesArray.slice(1),
    confidence: 0.6
  };
}

/**
 * Checkt of een post matcht met user's format preferences
 * Returns: priority score (0-100)
 */
export function calculateContentMatchScore(post: any, userFormatPrefs: string[] | undefined): number {
  if (!userFormatPrefs || userFormatPrefs.length === 0) {
    // No preferences set - all content is equal
    return 50;
  }
  
  const detection = detectContentType(post);
  
  // Check if primary type matches preferences
  if (userFormatPrefs.includes(detection.primaryType)) {
    return 100;
  }
  
  // Check if any secondary type matches
  const secondaryMatch = detection.secondaryTypes.some(type => userFormatPrefs.includes(type));
  if (secondaryMatch) {
    return 75;
  }
  
  // Check if it's text content and user has no specific preference
  if (detection.primaryType === 'text') {
    return 40;
  }
  
  // No match - deprioritize but don't hide completely
  return 25;
}

/**
 * Sorts posts based on user's format preferences
 * Posts matching user preferences appear first
 */
export function sortPostsByFormatPreference(posts: any[], userFormatPrefs: string[] | undefined): any[] {
  if (!userFormatPrefs || userFormatPrefs.length === 0) {
    // No filtering needed
    return posts;
  }
  
  return [...posts].sort((a, b) => {
    const scoreA = calculateContentMatchScore(a, userFormatPrefs);
    const scoreB = calculateContentMatchScore(b, userFormatPrefs);
    
    // Higher score first
    return scoreB - scoreA;
  });
}

/**
 * Filters posts to only show those matching user's format preferences
 * Use this for strict filtering (not recommended for initial release)
 */
export function filterPostsByFormatPreference(posts: any[], userFormatPrefs: string[] | undefined): any[] {
  if (!userFormatPrefs || userFormatPrefs.length === 0) {
    return posts;
  }
  
  return posts.filter(post => {
    const score = calculateContentMatchScore(post, userFormatPrefs);
    return score >= 50; // Only show posts with 50+ match score
  });
}
